/** @format */

import { Tab } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { useActiveFeatureFlags, usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import galaxieClient from "@/api/galaxieClient";
import { useUpdateUser, useUser } from "@/api/userAPI";
import { CalendarFeedSettings } from "@/components/CalendarFeedSettings";
import { NotificationSettings } from "@/components/NotificationSettings";
import { type UserProfileFormData, userProfileSchema } from "@/lib/schemas";
import { setFormErrorsFromApi } from "@/lib/utils/formErrorHandling";
import { trackEvent } from "@/utils/analytics";
import DriverMultiSelect from "@/utils/DriverMultiSelect";
import Loading from "@/utils/Loading";
import LoadingCircle from "@/utils/LoadingCircle";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const UserProfile = ({
  setModalComponent,
  setModalOpen,
  initialTab = "profile",
}: {
  setModalComponent: (component: string | null) => void;
  setModalOpen: (open: boolean) => void;
  initialTab?: "profile" | "notifications";
}) => {
  const { data: user, status: userStatus, error: userError } = useUser();
  const posthog = usePostHog();
  const flags = useActiveFeatureFlags();
  const notificationsEnabled = flags?.includes("notifications") ?? false;
  const {
    data: drivers,
    status: driverStatus,
    error: driverError,
  } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => galaxieClient.get<DriverList[]>("/drivers/").then(res => res.data),
  });
  const { mutateAsync } = useUpdateUser();
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UserProfileFormData>({
    resolver: zodResolver(userProfileSchema),
    defaultValues: {
      shortName: user?.shortName ?? "",
      fullName: user?.fullName ?? "",
      email1: user?.email ?? "",
      email2: user?.email ?? "",
      favoriteDrivers: user?.favoriteDrivers ?? [],
    },
  });

  const favoriteDrivers = watch("favoriteDrivers");
  const defaultTabIndex =
    initialTab === "notifications" && notificationsEnabled ? 1 : 0;

  const onSubmit = async (data: UserProfileFormData) => {
    try {
      await mutateAsync(data);
      trackEvent("favorite_drivers_updated", {
        count: data.favoriteDrivers?.length ?? 0,
        surface: "profile",
      });
      trackEvent("favorite_drivers_set", {
        count: data.favoriteDrivers?.length ?? 0,
        surface: "profile",
      });
      setSuccess(true);
    } catch (error: unknown) {
      setFormErrorsFromApi(error, setError, {
        email: "email1",
      });
      console.error(error);
    }
  };

  return success ? (
    <div className="self-center content-center bg-slate-950 rounded-xl p-6 w-full max-w-2xl border border-slate-600">
      <h1 className="text-2xl font-bold mb-2 text-white">Profile Updated</h1>
      <div className="text-slate-200">
        <p className="mb-6 text-sm">Your profile has been updated successfully.</p>
        <button
          type="button"
          onClick={() => {
            setModalComponent(null);
            setModalOpen(false);
          }}
          className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
        >
          Close
        </button>
      </div>
    </div>
  ) : (
    <div className="self-center bg-slate-950 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-600">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-600 bg-slate-900">
        <h1 className="text-2xl font-bold text-white">User Profile</h1>
        <p className="text-sm text-slate-400 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      <Loading
        statuses={[userStatus, driverStatus]}
        errors={[userError, driverError]}
        wrap={false}
      >
        <Tab.Group
          as="div"
          className="flex flex-col flex-1 min-h-0"
          defaultIndex={defaultTabIndex}
        >
          {/* Tab Navigation */}
          <Tab.List className="flex border-b border-slate-600 px-6 bg-slate-900 w-full">
            <Tab
              className={({ selected }) =>
                classNames(
                  "relative px-6 py-3 text-sm font-semibold transition-all",
                  "focus:outline-none focus:z-10",
                  selected ? "text-purple-300" : "text-slate-400 hover:text-slate-300"
                )
              }
            >
              {({ selected }) => (
                <>
                  👤 Profile
                  {selected && (
                    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-purple-500" />
                  )}
                </>
              )}
            </Tab>
            {notificationsEnabled && (
              <Tab
                className={({ selected }) =>
                  classNames(
                    "relative px-6 py-3 text-sm font-semibold transition-all",
                    "focus:outline-none focus:z-10",
                    selected ? "text-purple-300" : "text-slate-400 hover:text-slate-300"
                  )
                }
              >
                {({ selected }) => (
                  <>
                    🔔 Notifications
                    {selected && (
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-purple-500" />
                    )}
                  </>
                )}
              </Tab>
            )}
            <Tab
              className={({ selected }) =>
                classNames(
                  "relative px-6 py-3 text-sm font-semibold transition-all",
                  "focus:outline-none focus:z-10",
                  selected ? "text-purple-300" : "text-slate-400 hover:text-slate-300"
                )
              }
            >
              {({ selected }) => (
                <>
                  📅 Calendar
                  {selected && (
                    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-purple-500" />
                  )}
                </>
              )}
            </Tab>
          </Tab.List>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto w-full">
            <Tab.Panels className="w-full">
              {/* Profile Tab */}
              <Tab.Panel className="p-6 w-full max-w-full">
                <form
                  onSubmit={e => void handleSubmit(onSubmit)(e)}
                  className="space-y-6 w-full max-w-full"
                >
                  {/* Name Fields */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-200 mb-3">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="fullName"
                          className="block text-sm font-medium text-slate-200 mb-1.5"
                        >
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          autoComplete="name"
                          required
                          {...register("fullName")}
                          className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                        />
                        {errors.fullName && (
                          <div className="text-red-600 text-sm mt-1">
                            {errors.fullName.message}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="shortName"
                          className="block text-sm font-medium text-slate-200 mb-1.5"
                        >
                          Short Name
                        </label>
                        <input
                          type="text"
                          id="shortName"
                          autoComplete="nickname"
                          required
                          {...register("shortName")}
                          className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
                        />
                        {errors.shortName && (
                          <div className="text-red-600 text-sm mt-1">
                            {errors.shortName.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email1"
                      className="block text-sm font-medium text-slate-200 mb-1.5"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email1"
                      autoComplete="email"
                      required
                      disabled
                      {...register("email1")}
                      className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 disabled:cursor-not-allowed text-slate-400 sm:text-sm"
                    />
                    <p className="mt-1.5 text-xs text-slate-400">
                      Email cannot be changed. Contact support if needed.
                    </p>
                    {errors.email1 && (
                      <div className="text-red-600 text-sm mt-1">
                        {errors.email1.message}
                      </div>
                    )}
                  </div>

                  {/* Favorite Drivers */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-200 mb-3">
                      🏁 Favorite Drivers
                    </h3>
                    <DriverMultiSelect
                      selectedDrivers={favoriteDrivers}
                      setSelectedDrivers={(drivers: string[]) =>
                        setValue("favoriteDrivers", drivers)
                      }
                      drivers={drivers ?? []}
                    />
                  </div>

                  {/* Password Section */}
                  <div className="pt-6 border-t border-slate-600">
                    <h3 className="text-sm font-semibold text-slate-200 mb-3">
                      🔒 Security
                    </h3>
                    <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-slate-200 mb-1">
                            Password
                          </h4>
                          <p className="text-sm text-slate-400">
                            Keep your account secure with a strong password
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setModalOpen(true);
                            setModalComponent("resetPassword");
                          }}
                          className="px-4 py-2 rounded-md text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                        >
                          Change Password
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Debug Tools (Staff Only) */}
                  {user?.isStaff && (
                    <div className="pt-6 border-t border-slate-600">
                      <h3 className="text-sm font-semibold text-slate-200 mb-3">
                        🛠️ Developer Tools
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          className="px-4 py-2 text-sm text-white bg-slate-500 hover:bg-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                          type="button"
                          onClick={() => posthog.capture("Test Posthog")}
                        >
                          Test PostHog
                        </button>
                        <button
                          className="px-4 py-2 text-sm text-white bg-slate-500 hover:bg-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                          type="button"
                          onClick={() => {
                            Sentry.captureMessage(
                              "Sentry test message from UserProfile",
                              "info"
                            );
                            try {
                              throw new Error(
                                "Test error for Sentry - this is intentional"
                              );
                            } catch (error) {
                              Sentry.captureException(error);
                            }
                            Sentry.addBreadcrumb({
                              message: "User clicked Test Sentry button",
                              level: "info",
                              category: "user-action",
                            });
                            Sentry.setUser({
                              id: "test-user",
                              username: "sentry-tester",
                            });
                            console.info(
                              "Sentry tests sent! Check your Sentry dashboard for the events."
                            );
                          }}
                        >
                          Test Sentry
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Footer with buttons */}
                  <div className="flex justify-between gap-x-4 pt-4 border-t border-slate-600">
                    <button
                      type="button"
                      onClick={() => {
                        setModalComponent(null);
                        setModalOpen(false);
                      }}
                      disabled={isSubmitting}
                      className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || Object.keys(errors).length > 0}
                      className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <LoadingCircle className="h-5 w-5 mr-2" />
                          Saving
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                  </div>
                </form>
              </Tab.Panel>

              {/* Notifications Tab */}
              {notificationsEnabled && (
                <Tab.Panel className="p-6 w-full max-w-full">
                  <NotificationSettings />
                </Tab.Panel>
              )}

              {/* Calendar Tab */}
              <Tab.Panel className="p-6 w-full max-w-full">
                <CalendarFeedSettings />
              </Tab.Panel>
            </Tab.Panels>
          </div>
        </Tab.Group>
      </Loading>
    </div>
  );
};

export default UserProfile;
